// Gameplay sound effects: worm crash, power-up pickup, and round-over sting.
// All are one-shot and scheduled at an absolute time; the controller routes them
// through the fixed-level SFX bus (so the music-volume slider doesn't touch them).
// Tuned to the gameplay track's A-minor palette so pickups/round-end feel musical.

import { getNoiseBuffer, mtof, note, pluck } from "./synth";

// Switcharoonie pickup: a "swap" — two tones glide past each other in opposite
// directions (one up, one down), voices literally swapping registers, over a light
// noise whoosh. Distinct from the cheerful pickup arpeggio; A-minor palette (A4<->A5).
export function playSwitcharoonie(ctx: AudioContext, dest: AudioNode, time: number): void {
  const lo = mtof(note("A", 4));
  const hi = mtof(note("A", 5));
  const dur = 0.4;
  ([[lo, hi], [hi, lo]] as const).forEach(([from, to]) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(from, time);
    osc.frequency.exponentialRampToValueAtTime(to, time + dur);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, time);
    amp.gain.exponentialRampToValueAtTime(0.2, time + 0.02);
    amp.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    osc.connect(amp);
    amp.connect(dest);
    osc.start(time);
    osc.stop(time + dur + 0.05);
  });
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.setValueAtTime(0.8, time);
  bp.frequency.setValueAtTime(600, time);
  bp.frequency.exponentialRampToValueAtTime(3000, time + dur);
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.0001, time);
  ng.gain.exponentialRampToValueAtTime(0.1, time + 0.06);
  ng.gain.exponentialRampToValueAtTime(0.0001, time + dur);
  noise.connect(bp);
  bp.connect(ng);
  ng.connect(dest);
  noise.start(time);
  noise.stop(time + dur + 0.05);
}

// Switcharoonie portals opening: a dark riser matching the swap animation (the event-
// horizon discs growing + souls flying). A low drone swelling up a fifth (A2 -> E3)
// under a band-passed noise sweep that climbs the whole way, fading out right at go.
// The ONE sound of the sequence (an arrival pop/whump at go read as "something else
// happened" and was cut). `duration` should be SWITCH_ANIM_DURATION.
export function playPortalOpen(ctx: AudioContext, dest: AudioNode, time: number, duration = 1.2): void {
  // Rumbling drone: two slightly detuned saws through a closing-then-opening lowpass.
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(220, time);
  lp.frequency.exponentialRampToValueAtTime(900, time + duration);
  const dg = ctx.createGain();
  dg.gain.setValueAtTime(0.0001, time);
  dg.gain.exponentialRampToValueAtTime(0.16, time + duration * 0.35);
  dg.gain.setValueAtTime(0.16, time + duration * 0.85);
  dg.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  lp.connect(dg);
  dg.connect(dest);
  const lo = mtof(note("A", 2));
  const hi = mtof(note("E", 3));
  [-7, 7].forEach((cents) => {
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(lo, time);
    osc.frequency.exponentialRampToValueAtTime(hi, time + duration);
    osc.detune.setValueAtTime(cents, time);
    osc.connect(lp);
    osc.start(time);
    osc.stop(time + duration + 0.05);
  });
  // Airy rising whoosh over the drone: band-passed noise sweeping up.
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.setValueAtTime(1.1, time);
  bp.frequency.setValueAtTime(300, time);
  bp.frequency.exponentialRampToValueAtTime(4200, time + duration);
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.0001, time);
  ng.gain.exponentialRampToValueAtTime(0.09, time + duration * 0.5);
  ng.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  noise.connect(bp);
  bp.connect(ng);
  ng.connect(dest);
  noise.start(time);
  noise.stop(time + duration + 0.05);
}

// Worm crash: a short filtered-noise burst (the "crunch") plus a low sine zap that
// drops in pitch (the "thud"). Kept brief — it can fire often in a pile-up.
export function playCrash(ctx: AudioContext, dest: AudioNode, time: number): void {
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer(ctx);
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(2400, time);
  lp.frequency.exponentialRampToValueAtTime(240, time + 0.28);
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.0001, time);
  ng.gain.exponentialRampToValueAtTime(0.5, time + 0.005);
  ng.gain.exponentialRampToValueAtTime(0.0001, time + 0.32);
  noise.connect(lp);
  lp.connect(ng);
  ng.connect(dest);
  noise.start(time);
  noise.stop(time + 0.38);

  const zap = ctx.createOscillator();
  zap.type = "sine";
  zap.frequency.setValueAtTime(190, time);
  zap.frequency.exponentialRampToValueAtTime(45, time + 0.22);
  const zg = ctx.createGain();
  zg.gain.setValueAtTime(0.0001, time);
  zg.gain.exponentialRampToValueAtTime(0.45, time + 0.005);
  zg.gain.exponentialRampToValueAtTime(0.0001, time + 0.28);
  zap.connect(zg);
  zg.connect(dest);
  zap.start(time);
  zap.stop(time + 0.32);
}

// Power-up pickup: a bright ascending Am-triad arpeggio — a cheerful "ti-ti-ti".
export function playPowerup(ctx: AudioContext, dest: AudioNode, time: number): void {
  const notes = [note("A", 5), note("C", 6), note("E", 6)];
  notes.forEach((m, i) => {
    pluck(ctx, dest, {
      freq: mtof(m),
      time: time + i * 0.05,
      duration: 0.2,
      type: "triangle",
      gain: 0.28,
      attack: 0.004,
      cutoff: 6500,
    });
  });
}

// Round over: a short V -> i cadence (E major resolving to A minor) for a
// conclusive "round complete" feel, with a low root under each chord.
export function playRoundEnd(ctx: AudioContext, dest: AudioNode, time: number): void {
  const cadence = [
    { tones: [note("E", 4), note("G#", 4), note("B", 4)], root: note("E", 2), t: time, dur: 0.3 },
    { tones: [note("A", 3), note("C", 4), note("E", 4)], root: note("A", 2), t: time + 0.26, dur: 0.75 },
  ];
  cadence.forEach((step) => {
    step.tones.forEach((m) => {
      pluck(ctx, dest, {
        freq: mtof(m),
        time: step.t,
        duration: step.dur,
        type: "sawtooth",
        gain: 0.12,
        attack: 0.01,
        cutoff: 2600,
      });
    });
    pluck(ctx, dest, {
      freq: mtof(step.root),
      time: step.t,
      duration: step.dur,
      type: "sine",
      gain: 0.2,
      attack: 0.01,
      cutoff: 500,
    });
  });
}
